import { Component, OnDestroy } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntil, tap, flatMap, filter } from "rxjs/operators";
import { Subject, BehaviorSubject } from "rxjs";
import { BriefingService, StreamService } from "../services";
import * as moment from "moment";
import { HasChanges } from "../services/HasChanges";
import * as _ from 'lodash';
import { MatSnackBar } from "@angular/material/snack-bar";

@Component({
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Edit Briefing</h3>
                <span fxFlex></span>
                <button mat-raised-button color="primary" [disabled]="form?.invalid || !hasChanges()" (click)="save(true)">Save</button>
            </mat-toolbar>
            <briefing-editor [form]="form" 
                             [originalBriefing]="originalBriefing"
                             [topic$]="briefingStream$" 
                             [briefingId]="originalBriefing?.id"
                             (save)="save($event)"
                             (reorderTopics)="reorderTopics($event)"></briefing-editor>
        </div>
    `
})
export class EditBriefingPaneComponent implements HasChanges, OnDestroy { 
    form: FormGroup;
    cleanupTime$ = new Subject<void>();
    briefingStream$ = new BehaviorSubject<any[]>([]);
    briefingUser$ = new BehaviorSubject<any[]>([]);
    originalBriefing: any;
    hasSaved: boolean;
    readonly urlPattern = /^[A-Za-z][A-Za-z\d.+-]*:\/*(?:\w+(?::\w+)?@)?[^\s/]+(?::\d+)?(?:\/[\w#!:.?+=&%@\-/]*)?$/;

    constructor(route: ActivatedRoute,
                private router: Router,
                private snackBar: MatSnackBar,
                private briefingService: BriefingService,
                streamService: StreamService){
        this.form = briefingService.buildBriefingForm(this.cleanupTime$);
        this.form.controls.type.clearValidators(); // Validator not required for edit (and required is wrong for non slack)
        this.form.statusChanges.pipe(takeUntil(this.cleanupTime$)).subscribe(() => this.hasSaved = false);
        route.params.pipe(
            flatMap(params => briefingService.getBriefing(params.briefingId)),
            tap(briefing => this.originalBriefing = briefing),
            tap(briefing => {
                streamService.fetchBriefingStreams(briefing.id)
                .subscribe(streamRes => {
                    this.briefingStream$.next(this.sortedSources(this.originalBriefing, streamRes.content));
                });
            }),
            takeUntil(this.cleanupTime$)
        )
        .subscribe(briefing => {
            const patchBriefing = Object.assign({}, briefing); 
            if (briefing.type !== 'SLACK') {
                this.form.get('templateHeaderImageUrl').setValidators([Validators.pattern(this.urlPattern)]);
            }
            patchBriefing.schedule = Object.assign({}, { 
                timeZone: 'US/Pacific', 
                freq: 'DAILY',
                endMode: 'NEVER_ENDS',
                interval: 1 
            }, patchBriefing.schedule);
            this.form.patchValue(patchBriefing);
            this.form.get('schedule').get('sendTime').setValue(moment().set('hour', briefing.schedule.sendHour).set('minute', briefing.schedule.sendMinute).toDate());
        });
    }

    sortedSources(briefing, streams): any[] {
        const mappedStreams = streams.reduce((acc, stream) => {
            acc[stream.id] = stream;
            return acc;
        }, {});
        return briefing.streamIds?.map(streamId => mappedStreams[streamId]) || [];
    }

    hasChanges(): boolean {
        return !this.hasSaved && (this.form.dirty || !_.isEqual(this.originalBriefing?.streamIds || [], this.briefingStream$.value?.map(stream => stream.id)));
    }

    existingChangesMessage(): string {
        return 'Continue and lose changes?';
    }

    reorderTopics($event: { currentIndex: number, previousIndex: number }) {
        const sources = this.briefingStream$.value;
        sources.splice($event.currentIndex, 0, sources.splice($event.previousIndex, 1)[0]);
        this.originalBriefing.streamIds = this.briefingStream$.value.map(stream => stream.id);
        this.briefingStream$.next(this.sortedSources(this.originalBriefing, sources));
        this.save(true);
    }

    save(stayPut = false) {
        const briefing = Object.assign({}, this.originalBriefing, this.form.value, {
            streamIds: this.briefingStream$.value.map(stream => stream.id),
        });
        briefing.schedule = Object.assign({}, this.originalBriefing.schedule, briefing.schedule, {
            manual: briefing.schedule.scheduleMode === 'MANUAL',
            recurring: briefing.schedule.scheduleMode !== 'MANUAL',
            interval: 1,
            endMode: 'NEVER_ENDS',
            start: briefing.schedule.start || moment().toDate()
        });
        this.briefingService.saveBriefing(briefing)
        .pipe(
            tap(() => this.originalBriefing = briefing),
            tap(() => this.snackBar.open('Briefing Saved.', 'OK', { duration: 3000})),
            tap(() => this.hasSaved = true),
            filter(() => !stayPut),
            tap(() => this.router.navigate(['/settings', 'briefings']))
        )
        .subscribe();
    }

    ngOnDestroy(): void {
        this.cleanupTime$.next();
        this.cleanupTime$.complete();
    }
}